import { useMemo, useState, forwardRef, useImperativeHandle } from "react";
import Table from "@mui/material/Table";
import { useNavigate } from "react-router-dom";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import ColourConstants from "helpers/colourConstants";
import PopupMenu from "components/Elements/PopupMenu";
import { makeStyles } from "tss-react/mui";
import TableStyle from "styles/application/TableStyle";
import useInfiniteScrollSentinel, { scrollTableToTop } from "hooks/useInfiniteScrollSentinel";

// Icon imports
import { ReactComponent as MenuIcon } from "assets/icons/3dot-icon.svg";
import AutoFitContentInScreen from "components/Layouts/AutoFitContentInScreen";
import { createTableHeaderClickHandler, getLocalStorageData } from "helpers/utils";

// Init styled components
const AT = TableStyle();

// Size constant
const MAX_LOGO_HEIGHT = 47;

const useStyles = makeStyles()((theme) => ({
	tableBody: {
		whiteSpace: "noWrap",
	},

	lastCell: {
		borderBottom: "none",
	},
	table: {
		borderStyle: "solid",
		fontFamily: "Roboto Condensed",
		fontSize: 14,
		overflowX: "auto",
		borderColor: ColourConstants.tableBorder,
		borderWidth: 1,
		borderRadius: 0,
	},
	tableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackground,
		fontWeight: "bold",
		borderRightColor: "#979797",
		borderRightStyle: "solid",
		borderRightWidth: "1px",
	},
	selectedTableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackgroundSelected,
		fontWeight: "bold",
		color: "#FFFFFF",
	},
	nameRow: {
		width: "200px",
		height: "10px",
		lineHeight: "1rem",
	},
	clientsRow: {
		width: "20%",
	},
	dataCell: {
		height: 40,
	},
	nameLink: {
		color: ColourConstants.activeLink,
	},
	clientLogo: {
		maxHeight: MAX_LOGO_HEIGHT,
	},
	noImage: {
		color: ColourConstants.commonText,
		opacity: "50%",
	},
}));

const DefectListTable = forwardRef(({
	data,
	columns,
	headers,
	handleSort,
	handleDeleteDialogOpen,
	formattedData,
	currentTableSort,
	isReadOnly,
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
	isFetchNextPageError,
}, ref) => {
	// Init hooks
	const { classes, cx } = useStyles();
	const navigate = useNavigate();
	const { position } = getLocalStorageData("me");
	const canHighlightNew = position?.canAcknowledgeDefects === true;

	const [selectedData, setSelectedData] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);

	const { sentinelRef } = useInfiniteScrollSentinel({
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
		isError: isFetchNextPageError,
	});

	useImperativeHandle(ref, () => ({ gotoTop: () => scrollTableToTop() }));

	const formattedRows = useMemo(
		() => formattedData(data, navigate) || [],
		[data, navigate, formattedData]
	);

	const handleSortClick = createTableHeaderClickHandler(currentTableSort, handleSort);

	return (
		<>
			<AutoFitContentInScreen containsTable={true}>
				<Table aria-label="Table" className={classes.table}>
					<AT.TableHead className={classes.taskHeader}>
						<TableRow className={classes.tableHead}>
							{headers.map((header, index) => (
								<TableCell
									key={header.id}
									onClick={() => {
										header.disableSort !== true &&
											handleSortClick(columns[index]);
									}}
									style={{
										width: header?.width || "auto",
										minWidth: header?.minWidth || "auto",
									}}
									className={cx(classes.nameRow, {
										[classes.selectedTableHeadRow]:
											currentTableSort[0] === columns[index],
										[classes.tableHeadRow]:
											currentTableSort[0] !== columns[index],
									})}
								>
									<AT.CellContainer className="flex justify-between">
										{header.name}

										{header.disableSort !== true && (
											<div className="arrow">
												<AT.DescArrow
													fill={
														currentTableSort[0] === columns[index] &&
														currentTableSort[1] === "asc"
															? "#D2D2D9"
															: "#F9F9FC"
													}
													className="arrowUp"
												/>
												<AT.DefaultArrow
													fill={
														currentTableSort[0] === columns[index] &&
														currentTableSort[1] === "desc"
															? "#D2D2D9"
															: "#F9F9FC"
													}
													className="arrowDown"
												/>
											</div>
										)}
									</AT.CellContainer>
								</TableCell>
							))}
						</TableRow>
					</AT.TableHead>
					<TableBody className={classes.tableBody}>
						{formattedRows.map((row, index) => (
							<TableRow
								key={index}
								style={{
									backgroundColor:
										canHighlightNew && row?.isNew ? "#c8ebc8" : "transparent",
								}}
							>
								{columns?.map((col, i, arr) => (
									<TableCell
										key={col}
										style={{
											width: headers[i]?.width || "auto",
											minWidth: headers[i]?.minWidth || "auto",
											maxWidth: "500px",
											whiteSpace: "normal",
											fontWeight:
												canHighlightNew && row?.isNew ? 700 : "inherit",
										}}
									>
										<AT.CellContainer key={col}>
											{row[col]}

											{arr.length === i + 1 ? (
												<AT.DotMenu
													onClick={(e) => {
														setAnchorEl(
															anchorEl === e.currentTarget
																? null
																: e.currentTarget
														);
														setSelectedData(
															anchorEl === e.currentTarget ? null : index
														);
													}}
												>
													<AT.TableMenuButton>
														<MenuIcon />
													</AT.TableMenuButton>

													<PopupMenu
														index={index}
														selectedData={selectedData}
														anchorEl={anchorEl}
														isLast={index === data.length - 1}
														id={row.id}
														clickAwayHandler={() => {
															setAnchorEl(null);
															setSelectedData(null);
														}}
														menuData={
															isReadOnly
																? [
																		{
																			name: "View",
																			handler: () => {
																				navigate(`${row.id}`);
																			},
																			isDelete: false,
																		},
																  ]
																: [
																		{
																			name: "Edit",
																			handler: () => {
																				navigate(`${row.id}`);
																			},
																			isDelete: false,
																		},
																		{
																			name: "Remove",
																			handler: handleDeleteDialogOpen,
																			isDelete: true,
																		},
																  ]
														}
													/>
												</AT.DotMenu>
											) : null}
										</AT.CellContainer>
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
				{hasNextPage && (
					<div
						ref={sentinelRef}
						data-testid="infinite-scroll-sentinel"
						style={{ padding: "16px 10px" }}
					>
						{isFetchingNextPage && <b>Loading...</b>}
					</div>
				)}
				{!hasNextPage && data.length > 0 && (
					<div
						style={{ textAlign: "center", padding: "16px 10px" }}
						className="flex justify-center"
					>
						<span
							className="link-color ml-md cursor-pointer"
							onClick={() => ref?.current?.gotoTop?.()}
						>
							Go to top
						</span>
					</div>
				)}
			</AutoFitContentInScreen>
		</>
	);
});

export default DefectListTable;
